// Fetch user activity from on-chain events (newest first, max 100)
// Activities: Activated(1), Retopup(2), LevelIncome(3), DirectRefIncome(4),
// DividendSettled(5), Claimed(6), RoyaltyClaimed(7), AutoUpgrade(9)

import { NETWORK } from "../config/config";

const MAX_EVENTS = 100;
const CHUNK_SIZE = 50000; // blocks per scan
const MAX_CHUNKS = 20;    // safety cap (= ~1M blocks scanned max)

/**
 * Fetch up to 100 most recent activities (newest first) for a given user.
 * Scans backwards from the latest block in chunks, stops when 100 events found.
 */
export async function fetchActivityFromEvents(contracts, provider, userId) {
  if (!contracts || !provider || !userId) return [];

  const latest = await provider.getBlockNumber();
  const minBlock = NETWORK.deployBlock || 0;

  const { plan1, plan2, data } = contracts;
  const filterDefs = [
    { contract: plan1, name: "Activated", parser: parseActivated, plan: 1 },
    { contract: plan1, name: "Retopup", parser: parseRetopup, plan: 1 },
    { contract: plan1, name: "LevelIncome", parser: parseLevelIncome, plan: 1 },
    { contract: plan1, name: "DividendSettled", parser: parseDividend, plan: 1 },
    { contract: plan1, name: "AutoUpgrade", parser: parseAutoUpgrade, plan: 1 },
    { contract: plan1, name: "RoyaltyClaimed", parser: parseRoyalty, plan: 1 },
    { contract: plan2, name: "Activated", parser: parseActivated, plan: 2 },
    { contract: plan2, name: "Retopup", parser: parseRetopup, plan: 2 },
    { contract: plan2, name: "DirectRefIncome", parser: parseDirectRef, plan: 2 },
    { contract: plan2, name: "LevelIncome", parser: parseLevelIncome, plan: 2 },
    { contract: plan2, name: "DividendSettled", parser: parseDividend, plan: 2 },
    { contract: plan2, name: "AutoUpgrade", parser: parseAutoUpgrade, plan: 2 },
    { contract: plan2, name: "RoyaltyClaimed", parser: parseRoyalty, plan: 2 },
    { contract: data, name: "Claimed", parser: parseClaimed, plan: 0 },
  ];

  const collected = [];
  let toBlock = latest;
  let chunks = 0;

  while (toBlock >= minBlock && collected.length < MAX_EVENTS && chunks < MAX_CHUNKS) {
    const fromBlock = Math.max(toBlock - CHUNK_SIZE + 1, minBlock);

    // Run all 14 filters in parallel for this chunk
    const results = await Promise.all(filterDefs.map(async (f) => {
      try {
        const flt = f.contract.filters[f.name](userId);
        const events = await f.contract.queryFilter(flt, fromBlock, toBlock);
        return events.map((ev) => f.parser(ev, f.plan)).filter(Boolean);
      } catch {
        return [];
      }
    }));

    for (const r of results) collected.push(...r);
    toBlock = fromBlock - 1;
    chunks++;
  }

  // Sort newest first
  collected.sort((a, b) => {
    if (b.blockNumber !== a.blockNumber) return b.blockNumber - a.blockNumber;
    return (b.logIndex || 0) - (a.logIndex || 0);
  });

  // Cap at MAX_EVENTS
  const top = collected.slice(0, MAX_EVENTS);

  // Fetch block timestamps for the top events only
  const blocks = [...new Set(top.map((e) => e.blockNumber))];
  const blockTimes = {};
  await Promise.all(blocks.map(async (b) => {
    try {
      const block = await provider.getBlock(b);
      blockTimes[b] = block ? block.timestamp : 0;
    } catch { blockTimes[b] = 0; }
  }));

  for (const ev of top) ev.timestamp = blockTimes[ev.blockNumber] || 0;

  return top;
}

// ── Parsers ──
function parseActivated(ev, plan) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 1, plan, amount: ev.args.amt, pkg: ev.args.pkg.toNumber(), level: 0, fromUserId: 0 };
}
function parseRetopup(ev, plan) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 2, plan, amount: ev.args.amt, pkg: ev.args.pkg.toNumber(), level: 0, fromUserId: 0 };
}
function parseLevelIncome(ev, plan) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 3, plan, amount: ev.args.amt, pkg: ev.args.pkg.toNumber(),
    level: ev.args.lvl.toNumber(), fromUserId: ev.args.fromId.toNumber() };
}
function parseDirectRef(ev, plan) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 4, plan, amount: ev.args.amt, pkg: ev.args.pkg.toNumber(),
    level: 0, fromUserId: ev.args.fromId.toNumber() };
}
function parseDividend(ev, plan) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 5, plan, amount: ev.args.amt, pkg: ev.args.pkg.toNumber(), level: 0, fromUserId: 0 };
}
function parseClaimed(ev) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 6, plan: 0, amount: ev.args.amount, pkg: 0, level: 0, fromUserId: 0 };
}
function parseRoyalty(ev, plan) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 7, plan, amount: ev.args.amt, pkg: 0, level: 0, fromUserId: 0 };
}
function parseAutoUpgrade(ev, plan) {
  return { blockNumber: ev.blockNumber, logIndex: ev.logIndex, txHash: ev.transactionHash,
    actType: 9, plan, amount: ev.args.amt, pkg: ev.args.toPkg.toNumber(), level: 0, fromUserId: 0 };
}
